import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import zxcvbn from 'zxcvbn';

const USER_TYPES = [
  {
    value: 'client',
    label: 'Client',
    icon: 'User',
    description: 'Seeking legal services',
    color: 'text-blue-600',
    bg: 'bg-blue-50',
    border: 'border-blue-400',
    selectedBg: 'bg-blue-50'
  },
  {
    value: 'lawyer',
    label: 'Lawyer',
    icon: 'Scale',
    description: 'Licensed legal practitioner',
    color: 'text-primary',
    bg: 'bg-amber-50',
    border: 'border-primary',
    selectedBg: 'bg-amber-50'
  },
  {
    value: 'partner',
    label: 'Partner',
    icon: 'Briefcase',
    description: 'Firm partner or associate',
    color: 'text-purple-600',
    bg: 'bg-purple-50',
    border: 'border-purple-400',
    selectedBg: 'bg-purple-50'
  }
];

const RegistrationForm = ({ onSubmit, isLoading, error }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    userType: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');

  // Listen for terms acceptance from the T&C page (opened in new tab)
  useEffect(() => {
    // Check if already accepted (e.g. user navigated back)
    if (localStorage.getItem('termsAccepted') === 'true') {
      setTermsAccepted(true);
      setTermsError('');
    }
    const handleStorageChange = (e) => {
      if (e?.key === 'termsAccepted' && e?.newValue === 'true') {
        setTermsAccepted(true);
        setTermsError('');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Real-time password strength
  useEffect(() => {
    if (formData?.password) {
      const result = zxcvbn(formData?.password);
      setPasswordStrength(result);
    } else {
      setPasswordStrength(null);
    }
  }, [formData?.password]);

  // Real-time validation per field when touched
  useEffect(() => {
    if (Object.keys(touched)?.length > 0) {
      validateFields(touched);
    }
  }, [formData, touched]);

  const validateFields = (touchedFields = {}) => {
    const errors = {};

    if (touchedFields?.fullName !== undefined || touchedFields?.all) {
      if (!formData?.fullName?.trim()) {
        errors.fullName = 'Full name is required';
      } else if (formData?.fullName?.trim()?.length < 3) {
        errors.fullName = 'Full name must be at least 3 characters';
      } else if (!/^[a-zA-Z\s'-]+$/?.test(formData?.fullName?.trim())) {
        errors.fullName = 'Full name can only contain letters, spaces, hyphens and apostrophes';
      }
    }

    if (touchedFields?.email !== undefined || touchedFields?.all) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!formData?.email) {
        errors.email = 'Email is required';
      } else if (!emailRegex?.test(formData?.email)) {
        errors.email = 'Please enter a valid email address';
      }
    }

    if (touchedFields?.phone !== undefined || touchedFields?.all) {
      if (formData?.phone) {
        const cleanPhone = formData?.phone?.replace(/\s/g, '');
        const kenyanRegex = /^(\+254|0)(7|1)\d{8}$/;
        if (!kenyanRegex?.test(cleanPhone)) {
          errors.phone = 'Enter a valid Kenyan number (e.g. +254 700 000 000)';
        }
      } else {
        errors.phone = 'Phone number is required';
      }
    }

    if (touchedFields?.password !== undefined || touchedFields?.all) {
      if (!formData?.password) {
        errors.password = 'Password is required';
      } else if (formData?.password?.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      } else if (passwordStrength?.score < 2) {
        errors.password = 'Password is too weak. Add numbers, symbols or uppercase letters';
      }
    }

    if (touchedFields?.confirmPassword !== undefined || touchedFields?.all) {
      if (!formData?.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password';
      } else if (formData?.password !== formData?.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setValidationErrors(errors);
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handlePhoneChange = (e) => {
    let value = e?.target?.value;
    // Auto-format: if user types 07... convert to +2547...
    if (value?.startsWith('07') || value?.startsWith('01')) {
      value = '+254' + value?.slice(1);
    }
    setFormData(prev => ({ ...prev, phone: value }));
    setTouched(prev => ({ ...prev, phone: true }));
  };

  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleUserTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, userType: type }));
    if (validationErrors?.userType) {
      setValidationErrors(prev => ({ ...prev, userType: '' }));
    }
  };

  const handleTermsChange = (e) => {
    setTermsAccepted(e?.target?.checked);
    if (e?.target?.checked) setTermsError('');
  };

  const isFormValid = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const cleanPhone = formData?.phone?.replace(/\s/g, '');
    const kenyanRegex = /^(\+254|0)(7|1)\d{8}$/;
    return (formData?.fullName?.trim()?.length >= 3 &&
    /^[a-zA-Z\s'-]+$/?.test(formData?.fullName?.trim()) &&
    emailRegex?.test(formData?.email) &&
    kenyanRegex?.test(cleanPhone) &&
    formData?.password?.length >= 8 &&
    (passwordStrength?.score ?? 0) >= 2 &&
    formData?.password === formData?.confirmPassword &&
    formData?.userType !== '' && termsAccepted);
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setTouched({ all: true, fullName: true, email: true, phone: true, password: true, confirmPassword: true });
    const errors = validateFields({ all: true, fullName: true, email: true, phone: true, password: true, confirmPassword: true });

    if (!formData?.userType) {
      setValidationErrors(prev => ({ ...prev, userType: 'Please select your account type' }));
      return;
    }
    if (!termsAccepted) {
      setTermsError('You must accept the Terms & Conditions to continue');
      return;
    }
    if (Object?.keys(errors)?.length === 0 && formData?.userType && termsAccepted) {
      onSubmit({ ...formData, role: formData?.userType });
    }
  };

  const getPasswordStrengthColor = () => {
    if (!passwordStrength) return 'bg-gray-200';
    const colors = ['bg-red-500', 'bg-red-400', 'bg-yellow-500', 'bg-secondary', 'bg-green-500'];
    return colors?.[passwordStrength?.score] || 'bg-gray-200';
  };

  const getPasswordStrengthText = () => {
    if (!passwordStrength) return '';
    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    return labels?.[passwordStrength?.score] || '';
  };

  const getPasswordStrengthTextColor = () => {
    if (!passwordStrength) return 'text-text-secondary';
    const colors = ['text-red-500', 'text-red-400', 'text-yellow-600', 'text-secondary', 'text-green-600'];
    return colors?.[passwordStrength?.score] || 'text-text-secondary';
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth ${
      validationErrors?.[field] ? 'border-error bg-red-50' : touched?.[field] && !validationErrors?.[field] ? 'border-green-400' : 'border-border'
    }`;

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      {error && (
        <div className="bg-error-50 border border-error-100 text-error rounded-lg p-3 text-sm flex items-start gap-2">
          <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {/* User Type Selection */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          I am a <span className="text-error">*</span>
        </label>
        <div className="grid grid-cols-3 gap-2">
          {USER_TYPES?.map((type) => (
            <button
              key={type?.value}
              type="button"
              onClick={() => handleUserTypeSelect(type?.value)}
              className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                formData?.userType === type?.value
                  ? `${type?.border} ${type?.selectedBg} shadow-sm`
                  : 'border-border hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-1.5 ${
                formData?.userType === type?.value ? type?.bg : 'bg-gray-100'
              }`}>
                <Icon
                  name={type?.icon}
                  size={18}
                  className={formData?.userType === type?.value ? type?.color : 'text-text-secondary'}
                />
              </div>
              <span className={`text-xs font-semibold ${
                formData?.userType === type?.value ? type?.color : 'text-text-primary'
              }`}>
                {type?.label}
              </span>
              <span className="text-xs text-text-secondary text-center leading-tight mt-0.5">
                {type?.description}
              </span>
            </button>
          ))}
        </div>
        {validationErrors?.userType && (
          <p className="text-error text-xs mt-1 flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {validationErrors?.userType}
          </p>
        )}
      </div>
      {/* Full Name */}
      <div>
        <label htmlFor="fullName" className="block text-sm font-medium text-text-primary mb-2">
          Full Name <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type="text"
            id="fullName"
            name="fullName"
            value={formData?.fullName}
            onChange={handleInputChange}
            onBlur={() => handleBlur('fullName')}
            className={inputClass('fullName') + ' pr-10'}
            placeholder="Enter your full name (min. 3 characters)"
          />
          <div className="absolute right-3 top-3.5">
            {touched?.fullName && !validationErrors?.fullName && formData?.fullName ? (
              <Icon name="CheckCircle" size={18} className="text-green-500" />
            ) : (
              <Icon name="User" size={18} className="text-text-secondary" />
            )}
          </div>
        </div>
        {validationErrors?.fullName && (
          <p className="text-error text-xs mt-1 flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {validationErrors?.fullName}
          </p>
        )}
      </div>
      {/* Email */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
          Email Address <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type="email"
            id="email"
            name="email"
            value={formData?.email}
            onChange={handleInputChange}
            onBlur={() => handleBlur('email')}
            className={inputClass('email') + ' pr-10'}
            placeholder="your.email@example.com"
          />
          <div className="absolute right-3 top-3.5">
            {touched?.email && !validationErrors?.email && formData?.email ? (
              <Icon name="CheckCircle" size={18} className="text-green-500" />
            ) : (
              <Icon name="Mail" size={18} className="text-text-secondary" />
            )}
          </div>
        </div>
        {validationErrors?.email && (
          <p className="text-error text-xs mt-1 flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {validationErrors?.email}
          </p>
        )}
      </div>
      {/* Phone */}
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
          Phone Number <span className="text-error">*</span>
          <span className="text-xs text-text-secondary font-normal ml-1">(Kenyan format)</span>
        </label>
        <div className="relative">
          <div className="absolute left-3 top-3.5 flex items-center gap-1.5">
            <span className="text-sm">🇰🇪</span>
            <span className="text-text-secondary text-sm font-medium">+254</span>
            <span className="text-border">|</span>
          </div>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData?.phone}
            onChange={handlePhoneChange}
            onBlur={() => handleBlur('phone')}
            className={`w-full pl-20 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth ${
              validationErrors?.phone ? 'border-error bg-red-50' : touched?.phone && !validationErrors?.phone ? 'border-green-400' : 'border-border'
            }`}
            placeholder="700 000 000"
          />
          <div className="absolute right-3 top-3.5">
            {touched?.phone && !validationErrors?.phone && formData?.phone ? (
              <Icon name="CheckCircle" size={18} className="text-green-500" />
            ) : (
              <Icon name="Phone" size={18} className="text-text-secondary" />
            )}
          </div>
        </div>
        {validationErrors?.phone && (
          <p className="text-error text-xs mt-1 flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {validationErrors?.phone}
          </p>
        )}
        {!validationErrors?.phone && (
          <p className="text-text-secondary text-xs mt-1">Format: +254 7XX XXX XXX or 07XX XXX XXX</p>
        )}
      </div>
      {/* Password */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
          Password <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            id="password"
            name="password"
            value={formData?.password}
            onChange={handleInputChange}
            onBlur={() => handleBlur('password')}
            className={inputClass('password') + ' pr-12'}
            placeholder="Min. 8 characters with numbers & symbols"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-3.5 text-text-secondary hover:text-text-primary transition-smooth"
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        {/* Password Strength */}
        {formData?.password && (
          <div className="mt-2 space-y-1">
            <div className="flex items-center gap-2">
              <div className="flex-1 flex gap-1">
                {[0, 1, 2, 3, 4]?.map((i) => (
                  <div
                    key={i}
                    className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                      i <= (passwordStrength?.score ?? -1) ? getPasswordStrengthColor() : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <span className={`text-xs font-medium ${getPasswordStrengthTextColor()}`}>
                {getPasswordStrengthText()}
              </span>
            </div>
            {passwordStrength?.feedback?.warning && (
              <p className="text-xs text-yellow-600">{passwordStrength?.feedback?.warning}</p>
            )}
            {passwordStrength?.feedback?.suggestions?.[0] && (
              <p className="text-xs text-text-secondary">{passwordStrength?.feedback?.suggestions?.[0]}</p>
            )}
          </div>
        )}

        {validationErrors?.password && (
          <p className="text-error text-xs mt-1 flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {validationErrors?.password}
          </p>
        )}
      </div>
      {/* Confirm Password */}
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-2">
          Confirm Password <span className="text-error">*</span>
        </label>
        <div className="relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            id="confirmPassword"
            name="confirmPassword"
            value={formData?.confirmPassword}
            onChange={handleInputChange}
            onBlur={() => handleBlur('confirmPassword')}
            className={inputClass('confirmPassword') + ' pr-12'}
            placeholder="Re-enter your password"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-3.5 text-text-secondary hover:text-text-primary transition-smooth"
          >
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        {validationErrors?.confirmPassword && (
          <p className="text-error text-xs mt-1 flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {validationErrors?.confirmPassword}
          </p>
        )}
        {touched?.confirmPassword && !validationErrors?.confirmPassword && formData?.confirmPassword && (
          <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
            <Icon name="CheckCircle" size={12} />
            Passwords match
          </p>
        )}
      </div>
      {/* Terms & Conditions */}
      <div className="bg-gray-50 rounded-lg p-3 border border-border">
        <label className="flex items-start gap-3 cursor-pointer">
          <div className="relative flex-shrink-0 mt-0.5">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={handleTermsChange}
              className="sr-only"
            />
            <div
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                termsAccepted ? 'bg-primary border-primary' : 'bg-white border-border hover:border-primary'
              }`}
            >
              {termsAccepted && <Icon name="Check" size={12} color="white" />}
            </div>
          </div>
          <span className="text-sm text-text-secondary leading-relaxed">
            I agree to the{' '}
            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-medium hover:underline"
              onClick={(e) => e?.stopPropagation()}
            >
              Terms of Service
            </a>
            {' '}and{' '}
            <a
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary font-medium hover:underline"
              onClick={(e) => e?.stopPropagation()}
            >
              Privacy Policy
            </a>
            . I understand that my data will be processed in accordance with Kenyan data protection laws.
          </span>
        </label>
        {termsError && (
          <p className="text-error text-xs mt-2 flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {termsError}
          </p>
        )}
      </div>
      {/* Submit Button */}
      <button
        type="submit"
        disabled={isLoading || !isFormValid()}
        className={`w-full py-3 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth ${
          isFormValid() && !isLoading
            ? 'bg-primary text-white hover:bg-primary-700 cursor-pointer' :'bg-gray-200 text-gray-400 cursor-not-allowed'
        }`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Icon name="Loader2" size={20} className="animate-spin mr-2" />
            Creating Account...
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <Icon name="UserPlus" size={18} />
            Create Account
          </div>
        )}
      </button>
      {/* Form completion hint */}
      {!isFormValid() && !isLoading && (
        <p className="text-xs text-text-secondary text-center">
          Complete all required fields and accept terms to continue
        </p>
      )}
    </form>
  );
};

export default RegistrationForm;